import "twin.macro";

import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Link, useParams } from "react-router";

import { Close } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";

import _ from "lodash";

import { useSelectedResources } from "@features/filters";
import ProgramStatusChip from "@features/programs/ProgramStatusChip";
import { BlockCard, Type } from "@features/ui";
import { BlockCardActions, BlockCardNoContent } from "@features/ui/BlockCard";
import IndexTable from "@features/ui/indexTable";
import { CldImage } from "@services/cloudinary";

import { useOrderWindowQuery } from "../data";
import OrderWindowProgramTable from "./OrderWindowProgramTable";

const OrderWindowProgramsTile = () => {
  const { id } = useParams();
  const orderWindowId = id === "new" ? null : id!;
  const programIds = useWatch({ name: "programIds" });
  const channelId = useWatch({ name: "channelId" });
  const { setValue } = useFormContext();

  const { data: savedOrderWindow } = useOrderWindowQuery(orderWindowId);
  const savedOrderWindowPrograms = savedOrderWindow?.programs ?? [];

  const [isEditing, setIsEditing] = useState(false);

  const { data: loadedPrograms, isLoading } = useSelectedResources(
    "programs",
    programIds
  );
  const programs = isLoading ? savedOrderWindowPrograms : loadedPrograms;

  const handleRemoveProgram = (programId: string) => {
    setValue("programIds", _.without(programIds, programId), {
      shouldDirty: true,
    });
  };

  return (
    <BlockCard
      title="Programs"
      secondary={programIds.length > 0 && `${programIds.length} selected`}
    >
      {programIds.length === 0 && (
        <BlockCardNoContent>No programs selected</BlockCardNoContent>
      )}
      <IndexTable
        rows={_.sortBy(programs, "name")}
        columns={[
          {
            id: "cloudinaryId",
            label: "Image",
            render: (cldId) => (
              <div tw="w-20">
                <CldImage
                  tw="rounded-lg w-full aspect-square object-cover object-center"
                  publicId={cldId}
                  alt={""}
                  size="medium"
                />
              </div>
            ),
          },
          {
            id: "name",
            label: "Name",
            render: (_, program) => (
              <>
                <div tw="line-clamp-3">
                  <Link to={`/admin/programs/${program.id}`}>
                    <Type.H3 tw="font-medium hover:underline">
                      {program.name}
                    </Type.H3>
                  </Link>
                  <Type.P>{program.description}</Type.P>
                </div>
              </>
            ),
          },
          {
            id: "status",
            label: "Status",
            render: (_, program) => <ProgramStatusChip program={program} />,
          },
          {
            id: "itemIds",
            label: "Item Count",
            render: (itemIds: number[] | undefined) =>
              itemIds && (
                <p tw="mt-1.5 text-neutral-500">
                  {itemIds.length} item{itemIds.length === 1 ? "" : "s"}
                </p>
              ),
          },
          {
            id: "_remove",
            label: "",
            align: "right",
            render: (_, program) => (
              <Tooltip title="Remove Program" placement="top">
                <IconButton onClick={() => handleRemoveProgram(program.id)}>
                  <Close tw="text-neutral-400 text-lg" />
                </IconButton>
              </Tooltip>
            ),
          },
        ]}
      />

      <BlockCardActions>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsEditing(!isEditing)}
        >
          {programIds.length > 0 ? "Manage Programs" : "Add Programs"}
        </Button>
      </BlockCardActions>
      <Dialog
        open={isEditing}
        onClose={() => setIsEditing(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Manage Programs</DialogTitle>

        <DialogContent>
          {channelId && (
            <DialogContentText tw="mb-4">
              Only showing programs that are available for the selected channel.
            </DialogContentText>
          )}
          <OrderWindowProgramTable orderWindowId={orderWindowId} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIsEditing(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </BlockCard>
  );
};

export default OrderWindowProgramsTile;
